import { LendingPaymentSource } from '@varo/graphql-schema-types';
import backgroundTextureLight from 'assets/images/vxl/component/productIntroScreen/background-texture-light.png';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ImageSourcePropType,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { requestContentCardsRefresh } from 'services/engagement/braze/braze';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useEngagement } from 'shared/hooks/useBraze';
import CelebrationScreen from 'shared/patterns/vxl/CelebrationScreen/CelebrationScreen';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';

const RepaymentSuccessScreen = () => {
  const router = useRouter();
  const [isMounted, setIsMounted] = useState(false);
  const {
    id,
    message,
    pendingAmount,
    infoMessage,
    paymentSource,
    title,
    heroText,
  } = useLocalSearchParams<{
    id: string;
    message: string;
    pendingAmount: string;
    infoMessage: string;
    paymentSource: LendingPaymentSource;
    title: string;
    heroText: string;
  }>();

  const hasUrlParams =
    !!id && !!message && !!pendingAmount && !!paymentSource && !!title;

  const { refreshTiles, fetching } = useRefreshHomeTiles([
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU,
    HomeTileTypeIds.ADVANCE_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_BORROW,
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.NEXT_BEST_ACTION_TILE,
  ]);

  const { logCustomEvent, setRequestedCardId, requestedCard } = useEngagement();

  const isFullPayment = Number(pendingAmount) === 0;

  useViewDidFocusScreenAnalyticsEvent({
    key: '/accounts/varo-advance/confirm-payment/success',
    customAttributes: {
      name: isFullPayment ? 'VA Paid Off' : 'VA Repayment Received',
      payment_type: isFullPayment ? 'full' : 'partial',
    },
  });

  useEffect(() => {
    logCustomEvent('BRAZETRIGGER, repayment success screen viewed');
    requestContentCardsRefresh();
    setRequestedCardId?.('repayment_success_screen_offer');
  }, [logCustomEvent, setRequestedCardId]);

  useEffect(() => {
    setIsMounted(true);
    refreshTiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasUrlParams && isMounted) {
      router.navigate('/');
    }
  }, [hasUrlParams, isMounted, router]);

  const logButtonClickAnalytics = usePressableAnalytics({
    analyticsId: 'gotoHomeClicked',
    customAttributes: { cta: 'Done' },
  });

  const handleButtonPress = () => {
    logButtonClickAnalytics();

    if (Number(pendingAmount) === 0) {
      // Navigate user to `Home` screen in `All-paid-off` case
      router.dismissTo('/');
    } else {
      // Navigate user to Accounts screen
      router.dismissTo({
        pathname: '/accounts/[id]',
        params: {
          id,
        },
      });
    }
  };

  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'repaymentSuccessStrings',
  });

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
      >
        {paymentSource === LendingPaymentSource.ExternalAch ||
        Number(pendingAmount) !== 0 ? (
          <SuccessScreen
            primaryButton={{
              label: translatedText('buttons.done'),
              onPress: handleButtonPress,
            }}
            productOfferCard={requestedCard}
            visual="CASH_A"
            eyebrowText={heroText}
            titleText={title}
            content={
              <View style={styles.content}>
                <BodyBaseText style={styles.message} testID="Success Text">
                  {message}
                </BodyBaseText>
                <BodyXSText style={styles.description} testID="info-message">
                  {infoMessage}
                </BodyXSText>
              </View>
            }
          />
        ) : (
          <CelebrationScreen
            illustrationKey="PAID_OFF_A_ANIMATED"
            illustrationAccessibilityLabel="You are all paid off celebration animation"
            backgroundTexture={backgroundTextureLight as ImageSourcePropType}
            bodyText={message}
            captionText={infoMessage}
            button={{
              label: translatedText('buttons.done'),
              onPress: handleButtonPress,
              isLoading: fetching,
            }}
          />
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    gap: theme.spacing.m,
  },
  description: {
    color: theme.color.text.secondary,
    textAlign: 'center',
  },
  message: {
    textAlign: 'center',
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
});

export default RepaymentSuccessScreen;
